import { useBusy } from "@core-ui/AppBusy";
import { cn } from "@core-ui/utils/cn";
import t from "@ext/localization/locale/translate";

/**
 * Unsaved-work indicator: a dot pinned to the bottom-right corner of the screen.
 *
 * Fixed rather than inline on purpose. It first lived next to the breadcrumbs, right above the text
 * — but the breadcrumbs scroll away, so the dot disappeared exactly when it mattered: partway down a
 * long article, mid-typing. A corner is the only place that survives any scroll position.
 *
 * Stays in the DOM when saved so the state can be read, not inferred from an absent element.
 *
 * `role="img"`, not `role="status"`: status is a live region, and the dot flips on every debounce
 * cycle — a screen reader would read the save state out loud every few keystrokes. As a named
 * graphic the state is still there for anyone who looks, and nobody is interrupted.
 */
const SaveStatus = () => {
	const saving = useBusy("save");

	return (
		<span
			aria-label={saving ? t("article.save-pending") : t("article.save-done")}
			className={cn(
				"pointer-events-none fixed bottom-3 right-3 z-50 size-[5px] rounded-full bg-muted-foreground transition-opacity duration-300",
				saving ? "opacity-50" : "opacity-0",
			)}
			data-state={saving ? "saving" : "saved"}
			data-testid="save-status"
			role="img"
		/>
	);
};

export default SaveStatus;
